import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { SearchForm, SearchFormOptionItem } from "../../../Model/Dto/SearchForm";
import { SearchFormsService } from "../../../Services/SearchForms/searchforms.service";
import { LoginService } from "../../../Services/login.service";
import { TemplateFieldsService } from "../../../Services/templateFields.service";

@Component({
    selector: "searchFormOptions",
    templateUrl: "searchFormOptions.component.html",
    styleUrls: [],
    standalone: false
})
export class SearchFormOptionsComponent implements OnInit {

  private _refreshWithSearchForm: SearchForm;
  private initialized = false;

  @Input() set refreshWithSearchForm(value: SearchForm) {
    this._refreshWithSearchForm = value;
    if (this.initialized) {
      this.applyRefresh(value);
    }
  }
  get refreshWithSearchForm(): SearchForm {
    return this._refreshWithSearchForm;
  }

  @Input() syncWithService = true;
  @Output() onEditSearchForm = new EventEmitter<SearchForm>();
  @Output() onExecuteSearchForm = new EventEmitter<SearchForm>();

  allSearchForms: SearchForm[] = [];
  selectedSearchForm: SearchForm;

  private noValueOperators = new Set(['is_empty', 'is_not_empty', 'is_true', 'is_false']);
  private rangeOperators = new Set(['between', 'not_between']);

  constructor(
    public translate: TranslateService,
    private searchFormsService: SearchFormsService,
    public loginService: LoginService,
    private templateFieldsService: TemplateFieldsService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadSearchForms();
    this.applyActiveSearchForm();
    if (!this.selectedSearchForm && this._refreshWithSearchForm?.id) {
      const foundForm = this.allSearchForms.find(sf => sf.id === this._refreshWithSearchForm.id) ?? null;
      if (foundForm && this._refreshWithSearchForm.searchOptions?.length > 0) {
        foundForm.searchOptions = this._refreshWithSearchForm.searchOptions;
      }
      this.selectedSearchForm = foundForm;
      if (this.syncWithService) {
        this.searchFormsService.activeSearchForm = this.selectedSearchForm ?? null;
      }
    }
    this.initialized = true;
  }

  private async applyRefresh(searchForm: SearchForm) {
    await this.loadSearchForms();
    if (searchForm) {
      const foundForm = this.allSearchForms.find(sf => sf.id === searchForm.id) ?? null;
      if (foundForm && searchForm.searchOptions?.length > 0) {
        foundForm.searchOptions = searchForm.searchOptions;
      }
      this.selectedSearchForm = foundForm;
    } else {
      this.selectedSearchForm = null;
    }
    if (this.syncWithService) {
      this.searchFormsService.activeSearchForm = this.selectedSearchForm ?? null;
    }
    this.onExecuteSearchForm.emit(this.selectedSearchForm ?? null);
  }

  async loadSearchForms() {
    const customerId = this.loginService.currentUser.customerId;
    this.allSearchForms = await this.searchFormsService.getAll(customerId) ?? [];
    this.allSearchForms.forEach(sf => {
      (sf as any).disabled = sf.indexState !== 'ready_for_usage';
    });
    this.applyActiveSearchForm();
  }

  private applyActiveSearchForm() {
    if (!this.syncWithService) 
      return;
    const active = this.searchFormsService.activeSearchForm;
    if (active && !this.selectedSearchForm) {
      this.selectedSearchForm = this.allSearchForms.find(sf => sf.id === active.id) ?? null;
    }
    if (!active) {
      this.selectedSearchForm = null;
    }
  }

  executeSearchForm() {
    if (this.syncWithService) {
      this.searchFormsService.activeSearchForm = this.selectedSearchForm ?? null;
    }
    this.onExecuteSearchForm.emit(this.selectedSearchForm ?? null);
  }

  editSearchForm() {
    this.onEditSearchForm.emit(this.selectedSearchForm);
  }

  getFieldDisplayText(row: SearchFormOptionItem): string {
    const fieldName = row.nextPimField?.field;
    if (!fieldName) 
      return '';
    const field = this.templateFieldsService.translatedFields?.find(f => f.field === fieldName);
    if (!field || field.text === field.field) 
      return fieldName;
    return field.text;
  }

  isNoValue(row: SearchFormOptionItem): boolean {
    return this.noValueOperators.has(row.operator);
  }

  isRange(row: SearchFormOptionItem): boolean {
    return this.rangeOperators.has(row.operator);
  }

  getDataType(row: SearchFormOptionItem): string {
    const fieldName = row.nextPimField?.field;
    if (!fieldName) 
      return 'string';
    const field = this.templateFieldsService.translatedFields?.find(f => f.field === fieldName);
    return field?.dataType ?? 'string';
  }

}
